import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import ConfirmDialog from "../components/ConfirmDialog";
import { ButtonVariant } from "../components/Button";

/**
 * Promise-based replacement for `window.confirm`.
 *
 * Native browser dialogs are suppressed by several mobile browsers and
 * WebViews (the call silently returns false), which made buttons like
 * Restore/Delete do NOTHING on phones. Every confirmation must instead go
 * through the shared themed <ConfirmDialog>, and this context lets any
 * component await one without owning modal state:
 *
 *     const confirm = useConfirm();
 *     if (!(await confirm({ danger: true, entity: "Record" }))) return;
 *
 * The provider is mounted once in DefaultLayout, so every authenticated page
 * (and everything portaled from it — drawers included, since the Bootstrap
 * modal stacks above the side drawer at z-index 1090 > 1081) can use it.
 */
export interface ConfirmOptions {
    /** What the action targets (e.g. "Record", "Mission", "Photo"). Shown in the title. */
    entity?: string;
    /** Specific row/record name, rendered bold by ConfirmDialog's default body. */
    name?: string | null;
    /** Body copy. Defaults to ConfirmDialog's "Are you sure…" line built from entity/name. */
    message?: React.ReactNode;
    /** Title verb (e.g. "Delete", "Restore", "Complete"). Defaults by variant. */
    titleVerb?: string;
    titleIcon?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmVariant?: ButtonVariant;
    /** Destructive action: red button + warning icon + "Delete" verb defaults. */
    danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

/** Fallback when no provider is mounted (should not happen on authenticated
 *  pages) — degrade to the native dialog rather than silently refusing. */
const nativeFallback: ConfirmFn = (options) =>
    Promise.resolve(
        window.confirm(typeof options.message === "string" ? options.message : "Are you sure?")
    );

const ConfirmContext = createContext<ConfirmFn>(nativeFallback);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [options, setOptions] = useState<ConfirmOptions | null>(null);
    const resolverRef = useRef<((ok: boolean) => void) | null>(null);

    const confirm = useCallback((opts: ConfirmOptions) => {
        return new Promise<boolean>((resolve) => {
            // A confirm opened while another is pending cancels the first —
            // two overlapping questions can never both be on screen.
            resolverRef.current?.(false);
            resolverRef.current = resolve;
            setOptions(opts);
        });
    }, []);

    const settle = (ok: boolean) => {
        const resolve = resolverRef.current;
        resolverRef.current = null;
        setOptions(null);
        resolve?.(ok);
    };

    const danger = options?.danger ?? false;

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <ConfirmDialog
                show={options !== null}
                entity={options?.entity ?? ""}
                name={options?.name}
                message={options?.message}
                titleVerb={options?.titleVerb ?? (danger ? "Delete" : "Confirm")}
                titleIcon={options?.titleIcon ?? (danger ? "bi-exclamation-triangle" : "bi-question-circle")}
                confirmVariant={options?.confirmVariant ?? (danger ? "danger-solid" : "primary")}
                confirmLabel={options?.confirmLabel ?? (danger ? "Delete" : "Confirm")}
                cancelLabel={options?.cancelLabel ?? "Cancel"}
                testIdScope="global-confirm"
                onConfirm={() => settle(true)}
                onCancel={() => settle(false)}
            />
        </ConfirmContext.Provider>
    );
};

export const useConfirm = (): ConfirmFn => useContext(ConfirmContext);
